import { createPersistedSliceStorage, type PersistedSliceConfig } from "@mgdx/shared/src/utils/persistedSlice";
import { persist } from "zustand/middleware";
import { createStore } from "zustand/vanilla";

export type TalkState = {
  isEnabledTalk: boolean;
  isOpenTalk: boolean;
  selectedTalkRoomId?: string;
  talkOpenedFromFab: boolean;
};

export const initialTalkState: TalkState = {
  isEnabledTalk: false,
  isOpenTalk: false,
  selectedTalkRoomId: undefined,
  talkOpenedFromFab: false,
};

export type TalkActions = {
  enableTalk: () => void;
  disableTalk: () => void;
  openTalk: (roomId?: string) => void;
  openTalkFromFab: (roomId?: string) => void;
  closeTalk: () => void;
  clearSelectedTalkRoomId: () => void;
  clearTalkState: () => void;
};

export type TalkStore = TalkState & { actions: TalkActions };

const talkPersistConfig: PersistedSliceConfig<TalkState> = {
  key: "persist:talk",
  legacyReduxPersistVersion: -1,
  fields: ["isEnabledTalk", "isOpenTalk", "selectedTalkRoomId", "talkOpenedFromFab"],
};

export const createTalkStore = ({ persistent = false }: { persistent?: boolean } = {}) =>
  createStore<TalkStore>()(
    persist(
      (set) => ({
        ...initialTalkState,
        actions: {
          enableTalk: () => set({ isEnabledTalk: true }),
          disableTalk: () => set({ isEnabledTalk: false }),
          openTalk: (roomId) =>
            set({
              isOpenTalk: true,
              talkOpenedFromFab: false,
              ...(roomId === undefined ? {} : { selectedTalkRoomId: roomId }),
            }),
          openTalkFromFab: (roomId) =>
            set({
              isOpenTalk: true,
              talkOpenedFromFab: true,
              ...(roomId === undefined ? {} : { selectedTalkRoomId: roomId }),
            }),
          closeTalk: () => set({ isOpenTalk: false, talkOpenedFromFab: false }),
          clearSelectedTalkRoomId: () => set({ selectedTalkRoomId: undefined }),
          clearTalkState: () => set({ ...initialTalkState }),
        },
      }),
      {
        name: "persist:talk",
        skipHydration: true,
        storage: createPersistedSliceStorage(persistent ? talkPersistConfig : undefined, initialTalkState),
        partialize: ({ actions: _actions, ...state }) => state,
        merge: (persisted, current) => (persistent ? { ...current, ...(persisted as TalkState) } : current),
      }
    )
  );

export type TalkStoreApi = ReturnType<typeof createTalkStore>;
